import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import type { PlatformId } from '@kirin/api-client/contracts/platform-registry';
import { PERSONAL_MCP_FILTERABLE_FIELDS as MCP_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/account-mcp';
import {
  NameCell,
  ToolsCell,
  VersionCell,
  PublisherCell,
  TypeCell,
  TransportCell,
  SourcesCell,
  StatusCell,
  FirstSeenCell,
  LastSeenCell,
} from './CellRenderers';
import {
  MCPTypeFilterOption,
  MCPTransportFilterOption,
  MCPSourceFilterOption,
  MCPStatusFilterOption,
  MCPPublisherFilterOption,
  MCPVersionFilterOption,
} from './FilterComponents';
import {
  MCP_FILTER_URL_KEYS,
  MCP_TYPE_OPTIONS,
  MCP_TRANSPORT_OPTIONS,
  buildMcpSourceOptions,
  MCP_STATUS_OPTIONS,
} from './constants';
import {
  fetchPersonalMCPFieldFilterOptions,
  getPersonalMCPFilterOptionsQueryKey,
} from './api';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...EmptyOperators,
];

const StringOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
  ...EmptyOperators,
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...EmptyOperators,
];

/**
 * Column definitions for the personal MCP servers DataTable.
 * `releasedPlatforms` gates the Sources filter options.
 */
export function createPersonalMCPColumns(
  releasedPlatforms: ReadonlySet<PlatformId>
): ColumnConfig<MCPServerListItem>[] {
  return [
    {
      id: MCP_FILTERABLE_FIELDS.NAME,
      header: 'Name',
      accessor: 'name',
      width: 280,
      renderCell: ({ row }) => <NameCell row={row} />,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getPersonalMCPFilterOptionsQueryKey(MCP_FILTERABLE_FIELDS.NAME, {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params =>
          fetchPersonalMCPFieldFilterOptions(
            MCP_FILTERABLE_FIELDS.NAME,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.VERSION,
      header: 'Version',
      accessor: 'versions',
      width: 160,
      renderCell: ({ row }) => <VersionCell row={row} />,
      sortable: true,
      sortField: 'version',
      sortKey: 'v',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.VERSION],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getPersonalMCPFilterOptionsQueryKey(MCP_FILTERABLE_FIELDS.VERSION, {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params =>
          fetchPersonalMCPFieldFilterOptions(
            MCP_FILTERABLE_FIELDS.VERSION,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPVersionFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.PUBLISHER,
      header: 'Publisher',
      accessor: 'publisher',
      width: 160,
      renderCell: ({ row }) => <PublisherCell row={row} />,
      sortable: true,
      sortField: 'publisher',
      sortKey: 'pub',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.PUBLISHER],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getPersonalMCPFilterOptionsQueryKey(MCP_FILTERABLE_FIELDS.PUBLISHER, {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params =>
          fetchPersonalMCPFieldFilterOptions(
            MCP_FILTERABLE_FIELDS.PUBLISHER,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <MCPPublisherFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.TYPE,
      header: 'Type',
      accessor: 'type',
      width: 90,
      renderCell: ({ row }) => <TypeCell row={row} />,
      sortable: true,
      sortField: 'type',
      sortKey: 'ty',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TYPE],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_TYPE_OPTIONS,
        render: (option, isSelected) => (
          <MCPTypeFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.TRANSPORT,
      header: 'Transport',
      accessor: 'transport',
      width: 200,
      renderCell: ({ row }) => <TransportCell row={row} />,
      sortable: true,
      sortField: 'transport',
      sortKey: 'tr',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TRANSPORT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_TRANSPORT_OPTIONS,
        render: (option, isSelected) => (
          <MCPTransportFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.SOURCES,
      header: 'Sources',
      accessor: 'sources',
      width: 160,
      renderCell: ({ row }) => <SourcesCell row={row} />,
      sortable: true,
      sortField: 'sources',
      sortKey: 'sr',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.SOURCES],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: buildMcpSourceOptions(releasedPlatforms),
        render: (option, isSelected) => (
          <MCPSourceFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'tools',
      header: 'Tools',
      accessor: 'tools',
      renderCell: ({ row }) => <ToolsCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'tools',
      sortKey: 'to',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.TOOLS],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getPersonalMCPFilterOptionsQueryKey(MCP_FILTERABLE_FIELDS.TOOLS, {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params =>
          fetchPersonalMCPFieldFilterOptions(
            MCP_FILTERABLE_FIELDS.TOOLS,
            params
          ),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.RUNNING_COUNT,
      header: 'Status',
      accessor: 'runningCount',
      width: 110,
      renderCell: ({ row }) => <StatusCell row={row} />,
      sortable: true,
      sortField: 'runningCount',
      sortKey: 'rc',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.RUNNING_COUNT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: MCP_STATUS_OPTIONS,
        render: (option, isSelected) => (
          <MCPStatusFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT,
      header: 'First Seen',
      accessor: 'firstSeenAt',
      width: 130,
      renderCell: ({ row }) => <FirstSeenCell row={row} />,
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: 'fs',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: MCP_FILTERABLE_FIELDS.LAST_SEEN_AT,
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      width: 130,
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: MCP_FILTER_URL_KEYS[MCP_FILTERABLE_FIELDS.LAST_SEEN_AT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
